import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Link2, Download, Check, Zap, Users, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteData } from '../data/mock';

const OrcamentoDemo = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: FileText,
      title: 'Configure uma vez',
      description: 'Cadastre seus serviços e preços. Pronto.'
    },
    {
      icon: Link2,
      title: 'Link exclusivo',
      description: 'Compartilhe no WhatsApp ou Instagram.'
    },
    {
      icon: Download,
      title: 'PDF automático',
      description: 'Cliente recebe orçamento na hora.'
    }
  ];

  const benefits = [
    'Sem mais orçamentos manuais',
    'Atendimento 24h automatizado',
    'Mais tempo para trabalhar',
    'Imagem profissional'
  ];

  const templates = [
    { name: 'Marcenaria', icon: '🪚' },
    { name: 'Montagem', icon: '🔧' },
    { name: 'Oficina', icon: '🚗' },
    { name: 'Eletricista', icon: '⚡' },
    { name: 'Pintor', icon: '🎨' },
    { name: 'Personalizado', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header simples */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
          </a>
          <Button
            onClick={() => navigate('/orcamento/configurar')}
            className="btn-primary !py-2 !px-4 !min-h-0"
          >
            Testar grátis
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <Zap size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Serviço AtivaJá</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Orçamento na Hora
          </h1>
          
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Seus clientes selecionam os serviços, o sistema gera o orçamento em PDF.
            <br />
            <strong className="text-slate-800">Você não faz mais nada.</strong>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/orcamento/configurar')}
              className="btn-primary text-lg"
            >
              <Zap size={20} />
              Configurar meu orçamento
              <ArrowRight size={20} />
            </Button>
            <Button
              onClick={() => navigate('/orcamento/joao-montador')}
              variant="outline"
              className="btn-secondary text-lg"
            >
              Ver demonstração
            </Button>
          </div>
        </div>
      </section>

      {/* Como funciona */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Como funciona
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-black/5 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-6">
                  <feature.icon size={32} className="text-blue-600" />
                </div>
                <div className="text-sm font-medium text-blue-600 mb-2">Passo {index + 1}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates disponíveis */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-4">
            Templates prontos para seu negócio
          </h2>
          <p className="text-center text-slate-600 mb-12">
            Escolha um modelo ou crie do zero
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {templates.map((template, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-black/10 text-center hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="text-3xl mb-3">{template.icon}</div>
                <div className="text-sm font-medium text-slate-800">{template.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Pare de perder tempo com orçamentos
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-blue-600" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-black/5">
              <div className="text-center mb-6">
                <div className="text-sm text-slate-500 mb-1">A partir de</div>
                <div className="text-4xl font-bold text-slate-900">
                  R$ 29<span className="text-lg font-normal text-slate-500">,99/mês</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Link exclusivo personalizado
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Orçamentos ilimitados
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> PDF com sua logo
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-blue-600" /> Funciona 24h
                </li>
              </ul>
              <Button
                onClick={() => navigate('/orcamento/configurar')}
                className="btn-primary w-full"
              >
                Começar agora
              </Button>
              <p className="text-xs text-center text-slate-500 mt-4">
                7 dias grátis para testar
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Seu orçamento pode estar pronto em 5 minutos
          </h2>
          <p className="text-slate-600 mb-8">
            Configure uma vez. Compartilhe o link. Deixe o sistema trabalhar por você.
          </p>
          <Button
            onClick={() => navigate('/orcamento/configurar')}
            className="btn-primary text-lg"
          >
            <Zap size={20} />
            Ativar Orçamento na Hora
            <ArrowRight size={20} />
          </Button>
        </div>
      </section>

      {/* Footer simples */}
      <footer className="py-8 px-4 border-t border-black/5">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-6" />
            <span className="text-sm text-slate-500">Orçamento na Hora</span>
          </div>
          <p className="text-sm text-slate-500">Um serviço AtivaJá © {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default OrcamentoDemo;
